import React, { useCallback, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  RefreshControl,
  TouchableOpacity,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import AppHeader from '../components/AppHeader';
import { getPointExpiry } from '../services/pointsApi';

const formatNumber = (n) => Number(n || 0).toLocaleString('id-ID');

const HISTORY_LIMIT = 15;

const formatLotsLabel = (row) => {
  if (row?.lots_label) return row.lots_label;
  const lots = Number(row?.lots || 0);
  if (lots <= 1) return '';
  return `dari ${formatNumber(lots)} perolehan poin`;
};

export default function PointExpiryScreen() {
  const navigation = useNavigation();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [balance, setBalance] = useState(0);
  const [totalAkanHabis, setTotalAkanHabis] = useState(0);
  const [expiring, setExpiring] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError('');

      const response = await getPointExpiry(1, HISTORY_LIMIT);
      if (response?.status === 'success' && response.data) {
        setBalance(Number(response.data.balance || 0));
        setTotalAkanHabis(Number(response.data.total_akan_habis || 0));
        setExpiring(Array.isArray(response.data.expiring) ? response.data.expiring : []);
        setHistory(Array.isArray(response.data.history) ? response.data.history : []);
        setHistoryPage(Number(response.data.history_page || 1));
        setHistoryHasMore(Boolean(response.data.history_has_more));
      } else {
        setError(response?.message || 'Gagal memuat data kadaluwarsa poin');
      }
    } catch (e) {
      setError(e?.message || 'Gagal memuat data kadaluwarsa poin');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadMoreHistory = useCallback(async () => {
    if (loadingMore || !historyHasMore) return;
    try {
      setLoadingMore(true);
      const nextPage = historyPage + 1;
      const response = await getPointExpiry(nextPage, HISTORY_LIMIT);
      if (response?.status === 'success' && response.data) {
        const nextRows = Array.isArray(response.data.history) ? response.data.history : [];
        setHistory((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...nextRows.filter((r) => !seen.has(r.id))];
        });
        setHistoryPage(Number(response.data.history_page || nextPage));
        setHistoryHasMore(Boolean(response.data.history_has_more));
      }
    } catch (e) {
      setError(e?.message || 'Gagal memuat riwayat kadaluwarsa');
    } finally {
      setLoadingMore(false);
    }
  }, [historyHasMore, historyPage, loadingMore]);

  useFocusEffect(
    useCallback(() => {
      loadData(false);
    }, [loadData])
  );

  return (
    <SafeAreaView style={styles.container}>
      <AppHeader title="Point Kadaluwarsa" onBack={() => navigation.goBack()} />

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => loadData(true)} />
        }
      >
        <View style={styles.body}>
          <Text style={styles.subtitle}>
            Lihat kapan poin akan kadaluwarsa dan riwayat potongan otomatis.
          </Text>

          <View style={styles.summaryRow}>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Saldo poin</Text>
              <Text style={styles.summaryValue}>{formatNumber(balance)}</Text>
            </View>
            <View style={[styles.summaryCard, styles.summaryCardWarn]}>
              <Text style={styles.summaryLabel}>Akan kadaluwarsa</Text>
              <Text style={[styles.summaryValue, styles.summaryValueWarn]}>
                {formatNumber(totalAkanHabis)}
              </Text>
            </View>
          </View>

          {error ? (
            <View style={styles.errorBox}>
              <Text style={styles.errorText}>{error}</Text>
              <TouchableOpacity onPress={() => loadData(false)}>
                <Text style={styles.retryText}>Coba lagi</Text>
              </TouchableOpacity>
            </View>
          ) : null}

          {loading && !refreshing ? (
            <Text style={styles.loadingText}>Memuat...</Text>
          ) : (
            <>
              <Text style={styles.sectionTitle}>Akan kadaluwarsa</Text>
              {expiring.length === 0 ? (
                <View style={styles.emptyCard}>
                  <Text style={styles.emptyText}>Tidak ada poin yang akan kadaluwarsa.</Text>
                </View>
              ) : (
                expiring.map((row, idx) => {
                  const lotsLabel = formatLotsLabel(row);
                  return (
                    <View key={`exp-${row.tanggal_expired}-${idx}`} style={styles.itemCard}>
                      <View style={styles.itemLeft}>
                        <Text style={styles.itemPoints}>{formatNumber(row.points)} poin</Text>
                        <Text style={styles.itemMeta}>
                          Kadaluwarsa pada {row.tanggal_label || '—'}
                          {lotsLabel ? ` · ${lotsLabel}` : ''}
                        </Text>
                      </View>
                      <View style={styles.badgeWarn}>
                        <Text style={styles.badgeWarnText}>Akan kadaluwarsa</Text>
                      </View>
                    </View>
                  );
                })
              )}

              <Text style={[styles.sectionTitle, styles.sectionTitleSpaced]}>
                Riwayat potongan kadaluwarsa
              </Text>
              {history.length === 0 ? (
                <View style={styles.emptyCard}>
                  <Text style={styles.emptyText}>Belum ada riwayat potongan kadaluwarsa.</Text>
                </View>
              ) : (
                history.map((row) => (
                  <View key={`hist-${row.id}`} style={styles.itemCard}>
                    <View style={styles.itemLeft}>
                      <Text style={[styles.itemPoints, styles.itemPointsNeg]}>
                        −{formatNumber(row.points)} poin
                      </Text>
                      <Text style={styles.itemMeta} numberOfLines={2}>
                        {row.keterangan || 'Poin kadaluwarsa'}
                      </Text>
                      <Text style={styles.itemDate}>{row.created_label || ''}</Text>
                    </View>
                    <View style={styles.badgeMuted}>
                      <Text style={styles.badgeMutedText}>Kadaluwarsa</Text>
                    </View>
                  </View>
                ))
              )}

              {historyHasMore ? (
                <TouchableOpacity
                  style={styles.loadMoreButton}
                  onPress={loadMoreHistory}
                  disabled={loadingMore}
                >
                  <Text style={styles.loadMoreText}>
                    {loadingMore ? 'Memuat...' : 'Lihat Lebih Banyak'}
                  </Text>
                </TouchableOpacity>
              ) : null}

              <TouchableOpacity
                style={styles.tukarButton}
                onPress={() => navigation.navigate('MainTabs', { screen: 'TukarPoin' })}
              >
                <Ionicons name="gift-outline" size={18} color="#FFFFFF" />
                <Text style={styles.tukarButtonText}>Tukar poin sekarang</Text>
              </TouchableOpacity>
            </>
          )}

          <View style={styles.bottomSpacing} />
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
  },
  // Same horizontal inset as PointsScreen (historyContainer / pointsHeaderContainer)
  body: {
    paddingHorizontal: 20,
  },
  subtitle: {
    marginTop: 16,
    marginBottom: 16,
    fontSize: 13,
    lineHeight: 20,
    color: '#64748B',
  },
  summaryRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 20,
  },
  summaryCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  summaryCardWarn: {
    borderColor: '#FDE68A',
    backgroundColor: '#FFFBEB',
  },
  summaryLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 6,
  },
  summaryValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#0F172A',
  },
  summaryValueWarn: {
    color: '#D97706',
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 10,
  },
  sectionTitleSpaced: {
    marginTop: 22,
  },
  itemCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    marginBottom: 10,
  },
  itemLeft: {
    flex: 1,
  },
  itemPoints: {
    fontSize: 15,
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: 4,
  },
  itemPointsNeg: {
    color: '#DC2626',
  },
  itemMeta: {
    fontSize: 13,
    color: '#64748B',
    lineHeight: 18,
  },
  itemDate: {
    marginTop: 4,
    fontSize: 12,
    color: '#94A3B8',
  },
  badgeWarn: {
    backgroundColor: '#FEF3C7',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  badgeWarnText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#B45309',
  },
  badgeMuted: {
    backgroundColor: '#F1F5F9',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  badgeMutedText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#475569',
  },
  emptyCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  emptyText: {
    fontSize: 13,
    color: '#64748B',
  },
  loadMoreButton: {
    marginTop: 4,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  loadMoreText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#334155',
  },
  tukarButton: {
    marginTop: 24,
    backgroundColor: '#F59E0B',
    borderRadius: 12,
    paddingVertical: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  tukarButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '600',
  },
  loadingText: {
    marginTop: 24,
    textAlign: 'center',
    color: '#64748B',
  },
  errorBox: {
    backgroundColor: '#FEF2F2',
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
  },
  errorText: {
    color: '#B91C1C',
    fontSize: 13,
    marginBottom: 8,
  },
  retryText: {
    color: '#B45309',
    fontWeight: '600',
    fontSize: 13,
  },
  bottomSpacing: {
    height: 40,
  },
});
